import { MarkerType } from '@web/components/Map/Map.constants'
import { DEFAULT_ZOOM_LEVEL } from '@web/components/Map/New/constant'
import { getMarkerIconAnchor, getMarkerSize } from '@web/components/Map/New/marker.unit'
import { CustomMarkerOption } from '@web/components/Map/New/types'
import { Coordinate } from '@web/federation/gql/graphql'

export function calculateZoomLevelByCoords(
  coords: naver.maps.LatLngObjectLiteral[],
  mapWidth: number,
  mapHeight: number,
  minZoom = 11,
  defaultZoom = DEFAULT_ZOOM_LEVEL,
  maxZoom = 16,
): number {
  if (coords.length === 0) return defaultZoom

  const R = 6378137 // 지구 반지름 (meters)

  // 위도와 경도를 라디안으로 변환하는 함수
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180

  // Web Mercator 좌표로 변환하는 함수 (위도와 경도를 평면상의 좌표로 변환)
  const latLngToPoint = (latLng: naver.maps.LatLngObjectLiteral) => {
    const x = R * toRadians(latLng.lng)
    const y = R * Math.log(Math.tan(Math.PI / 4 + toRadians(latLng.lat) / 2))
    return { x, y }
  }

  // 모든 좌표를 Web Mercator 좌표로 변환
  const points = coords.map(latLng => latLngToPoint(latLng))

  // x, y 좌표의 최대 최소값 계산
  const maxX = Math.max(...points.map(p => p.x))
  const minX = Math.min(...points.map(p => p.x))
  const maxY = Math.max(...points.map(p => p.y))
  const minY = Math.min(...points.map(p => p.y))

  // 너비와 높이를 미터 단위로 계산 (최대 x값에서 최소 x값을 뺀 것이 너비, y도 마찬가지)
  const widthMeters = maxX - minX
  const heightMeters = maxY - minY

  // 좌표 간의 거리가 없거나, 잘못된 값이면 최대 줌 레벨 반환 (너비나 높이가 0 이하일 경우)
  if (widthMeters <= 0 || heightMeters <= 0) return maxZoom

  // 줌 레벨 0에서의 1 픽셀당 미터 수 (156543.03392는 고정된 값으로 사용)
  const metersPerPxAtZoom0 = 156543.03392

  // 화면 크기와 경계 박스의 크기를 비교하여 적절한 metersPerPx 계산
  const metersPerPx = Math.max(widthMeters / mapWidth, heightMeters / mapHeight)

  // 줌 레벨 계산 (로그를 사용하여 줌 레벨을 계산. 미터 수를 픽셀 수로 나눈 값을 log2로 계산)
  const zoom = Math.log2(metersPerPxAtZoom0 / metersPerPx)

  // 계산된 줌 레벨을 최소 줌 레벨과 최대 줌 레벨 사이로 제한하고 반환
  return Math.max(minZoom, Math.min(maxZoom, Math.floor(zoom)))
}

export const getBoundCenter = (coords: naver.maps.LatLngObjectLiteral[]): naver.maps.LatLngObjectLiteral | null => {
  if (!coords.length) return null

  // @ts-ignore
  const bounds = new naver.maps.LatLngBounds()

  coords.forEach(coord => {
    bounds.extend(new naver.maps.LatLng(coord.lat, coord.lng))
  })

  const center = bounds.getCenter()

  return pointToMapLatLng(center)
}

/**
 * 중심좌표를 bottomOffset 만큼 보정한 좌표 반환
 */
export const getCenterWithOffset = ({
  bottomOffset,
  center,
  map,
}: {
  center: naver.maps.LatLngObjectLiteral
  map: naver.maps.Map
  bottomOffset: number
}): naver.maps.LatLngObjectLiteral => {
  const proj = map.getProjection()
  const centerOffset = proj.fromCoordToOffset(new naver.maps.LatLng(center))
  const adjustedCenterOffset = new naver.maps.Point(centerOffset.x, centerOffset.y + bottomOffset / 2)
  const adjustedCenter = proj.fromOffsetToCoord(adjustedCenterOffset)
  return pointToMapLatLng(adjustedCenter)
}

export function calculateZoomLevelIncludeBottomSheet({
  map,
  markers,
  bottomSheetHeight,
  minZoom,
  defaultZoom = 10,
  maxZoom,
}: {
  map: naver.maps.Map
  markers: CustomMarkerOption[]
  bottomSheetHeight: number
  minZoom?: number
  defaultZoom?: number
  maxZoom?: number
}) {
  const mapSize = map.getSize()

  const targetCoords = markers.map(option => option.position)

  const zoomLevel = calculateZoomLevelByCoords(
    targetCoords,
    mapSize.width,
    mapSize.height - bottomSheetHeight,
    minZoom ?? map.getMinZoom(),
    defaultZoom,
    maxZoom ?? map.getMaxZoom(),
  )

  return zoomLevel
}

export const updateCenterByMarker = ({
  map,
  marker,
  bottomSheetHeight,
}: {
  map: naver.maps.Map
  marker: CustomMarkerOption
  bottomSheetHeight: number
}) => {
  const center = getCenterByMarkerOption({ map, markerOption: marker })
  const adjustedCenter = getCenterWithOffset({ map, bottomOffset: bottomSheetHeight, center })
  map.panTo(adjustedCenter)
}

export const coordinateToMapLatLng = ({ latitude, longitude }: Coordinate): naver.maps.LatLngObjectLiteral => ({
  lat: latitude,
  lng: longitude,
})

export const pointToMapLatLng = ({ x, y }: naver.maps.PointObjectLiteral): naver.maps.LatLngObjectLiteral => ({
  lat: y,
  lng: x,
})

/**
 * 사이즈가 있는 마커의 경우, 해당 마커의 중심이 지도 중심에 위치하도록 보정 중심좌표를 구하는 함수
 */
export function getAdjustedCenterByMakrerAnchor({
  map,
  marker,
}: {
  map: naver.maps.Map
  marker: Pick<CustomMarkerOption, 'position' | 'icon'>
}): naver.maps.LatLngObjectLiteral {
  const { position } = marker

  const anchor = getMarkerIconAnchor(marker.icon)

  let adjustedPosition: naver.maps.Coord = new naver.maps.LatLng(position)

  if (anchor) {
    const { height } = anchor
    const offsetY = height / 2

    const proj = map.getProjection()
    const point = proj.fromCoordToOffset(adjustedPosition)

    const adjustedPoint = new naver.maps.Point(point.x, point.y - offsetY)
    adjustedPosition = proj.fromOffsetToCoord(adjustedPoint)
  }

  return pointToMapLatLng(adjustedPosition)
}

export const getCenterByMarkerOption = ({
  map,
  markerOption,
}: {
  map: naver.maps.Map
  markerOption: CustomMarkerOption
}): naver.maps.LatLngObjectLiteral => {
  const anchor = getMarkerIconAnchor(markerOption.icon)
  const center = anchor ? getAdjustedCenterByMakrerAnchor({ map, marker: markerOption }) : markerOption.position
  return center
}

/**
 * 두 마커의 겹침 여부를 판단합니다.
 * @param p1 우선순위 마커
 * @param p2 후순위 마커
 * @param projection 맵 투영체
 * @param limitDistance 겹침 임계 거리
 * @returns 겹치는지 여부
 */
export const isMarkerOverlapped = ({
  limitDistance,
  p1,
  p2,
  projection,
}: {
  p1: CustomMarkerOption
  p2: CustomMarkerOption
  projection: naver.maps.MapSystemProjection
  limitDistance: number
}): boolean => {
  const p1Size = getMarkerSize(p1)

  const p1Pos = projection.fromCoordToOffset(new naver.maps.LatLng(p1.position))
  const p2Pos = projection.fromCoordToOffset(new naver.maps.LatLng(p2.position))

  // 사진마커의 경우 다른 마커의 중심을 가리는지 체크
  if (
    (p1.icon.type === MarkerType.THUMBNAIL || p1.icon.type === MarkerType.THUMBNAIL_SMALL) &&
    (p2.icon.type === MarkerType.THUMBNAIL || p2.icon.type === MarkerType.THUMBNAIL_SMALL)
  ) {
    const horizontalOverlap = Math.abs(p1Pos.x - p2Pos.x) <= p1Size.width / 2
    const verticalOverlap = Math.abs(p1Pos.y - p2Pos.y) <= p1Size.height / 2
    return horizontalOverlap && verticalOverlap
  }

  // 기타 마커는 거리로 판단
  return calculateDistance(p1.position, p2.position) <= limitDistance
}

/**
 * 두 좌표 사이의 거리를 반환하는 함수
 * @param pos1 첫 번째 좌표 (위도, 경도)
 * @param pos2 두 번째 좌표 (위도, 경도)
 * @returns 두 좌표 사이의 거리(미터 단위)
 */
export function calculateDistance(pos1: naver.maps.LatLngObjectLiteral, pos2: naver.maps.LatLngObjectLiteral): number {
  const R = 6371000 // 지구의 반지름 (미터 단위)
  const rad = Math.PI / 180 // 각도를 라디안으로 변환하기 위한 상수

  // 위도와 경도를 라디안으로 변환
  const lat1 = pos1.lat * rad
  const lat2 = pos2.lat * rad
  const deltaLat = (pos2.lat - pos1.lat) * rad
  const deltaLng = (pos2.lng - pos1.lng) * rad

  // Haversine 공식을 사용한 거리 계산
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c // 두 좌표 간의 거리 (미터)

  return distance
}
